import { Clapperboard } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { Button, type ButtonProps } from "@/components/ui/primitives"
import { UI_ACCENT_FILLED } from "@/lib/ui-selection"
import { cn } from "@/lib/utils"
import { ADAPTIVE_DURATION_SEC } from "@/lib/video-duration"
import { useTranslation } from "@/hooks/use-translation"

type VideoDurationPopoverProps = {
  value: number
  options: readonly number[]
  onChange: (duration: number) => void
  placement?: "above" | "below"
  multiShot?: {
    checked: boolean
    onChange: (multi: boolean) => void
  }
  className?: string
  triggerVariant?: ButtonProps["variant"]
  triggerClassName?: string
}

export function VideoDurationPopover({
  value,
  options,
  onChange,
  placement = "below",
  multiShot,
  className,
  triggerVariant = "outline",
  triggerClassName,
}: VideoDurationPopoverProps) {
  const { t } = useTranslation(["shot", "common"])
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)

  const { menuStyle, portalRoot } = useDropdownMenu({
    open,
    direction: placement === "above" ? "up" : "down",
    align: "left",
    anchorRef: buttonRef,
    menuRef,
    rootRef,
    resolveWidth: () => ({ width: "12rem" }),
    onOutsideClick: () => setOpen(false),
  })

  useEffect(() => {
    if (!open) return
    function handleKey(e: globalThis.KeyboardEvent) {
      if (e.key !== "Escape") return
      e.preventDefault()
      e.stopPropagation()
      setOpen(false)
    }
    document.addEventListener("keydown", handleKey, true)
    return () => document.removeEventListener("keydown", handleKey, true)
  }, [open])

  if (options.length === 0) return null

  const handleSelect = (dur: number) => {
    onChange(dur)
    setOpen(false)
  }

  const durationLabel = (d: number) => (d === ADAPTIVE_DURATION_SEC ? t("common.durationAuto") : `${d}s`)

  const menu =
    open && menuStyle && portalRoot ? (
      <div
        ref={menuRef}
        style={menuStyle}
        className="rounded-lg border border-ui-border-strong p-2.5 shadow-xl shadow-black/50 backdrop-blur-md dark:bg-ui-bg-elevated/95 light:bg-ui-surface/95"
      >
        <p className="mb-1.5 text-[10px] font-medium tracking-wide text-ui-text-secondary uppercase">
          {t("common.duration")}
        </p>
        <div className="flex flex-wrap gap-1">
          {options.map((d) => (
            <button
              key={d}
              type="button"
              className={cn(
                "rounded-md px-2 py-1 text-[11px] font-medium tabular-nums transition-colors",
                d === value
                  ? UI_ACCENT_FILLED
                  : "bg-ui-surface-raised text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary",
              )}
              onClick={() => handleSelect(d)}
            >
              {durationLabel(d)}
            </button>
          ))}
        </div>
        {multiShot ? (
          <div className="mt-2.5 border-t border-ui-border pt-2">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={multiShot.checked}
                onChange={(e) => multiShot.onChange(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-zinc-600 text-violet-500 accent-violet-500 dark:bg-zinc-800 light:bg-clay-2"
              />
              <span className="text-[11px] text-ui-text-secondary">{t("common.multishot")}</span>
            </label>
          </div>
        ) : null}
      </div>
    ) : null

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <Button
        ref={buttonRef}
        type="button"
        variant={triggerVariant}
        size="sm"
        className={cn(
          "gap-1.5",
          triggerVariant === "outline" && "border-ui-border bg-ui-surface-sunken hover:bg-ui-surface-sunken",
          triggerClassName,
        )}
        onClick={() => setOpen((v) => !v)}
        title={t("common.videoDuration")}
      >
        <Clapperboard className="h-3.5 w-3.5" />
        <span className="tabular-nums">{durationLabel(value)}</span>
      </Button>
      {menu && portalRoot ? createPortal(menu, portalRoot) : null}
    </div>
  )
}
